/**
 * External dependencies
 */
import * as React from 'react';
import { useEffect, useRef, useDebouncedCallback } from '@web-stories-wp/react';
import { useSnackbar } from '@web-stories-wp/design-system';
import {
  getPatternAsString,
  hasGradient,
  isPatternEqual,
} from '@web-stories-wp/patterns';
/**
 * Internal dependencies
 */
import { useAPI } from '../api';
import { useStory } from '../story';
import { CSS_VARIABLE_PREFIX, SAVED_COLORS_STYLE_TAG_ID } from './constants';
import type { SavedColor } from './types';

function SavedColorsWrapper({ children }: React.PropsWithChildren<{}>) {
  const { showSnackbar } = useSnackbar();

  const { actions: apiActions } = useAPI();

  const {
    pages,
    savedColors = [],
    updateSavedColors,
    updateSavedColorState,
  } = useStory(
    ({
      actions: { updateSavedColors, updateSavedColorState },
      state: { pages, savedColors },
    }) => {
      return {
        pages,
        savedColors,
        updateSavedColors,
        updateSavedColorState,
      };
    }
  );

  const lastSavedColors = useRef<SavedColor[]>([]);
  const initialGetSavedColors = useRef(apiActions.getSavedColors);
  const initialUpdateSavedColors = useRef(updateSavedColors);

  useEffect(() => {
    const abortController = new AbortController();

    initialGetSavedColors
      .current({ signal: abortController.signal })
      .then(({ colors }) => {
        if (abortController.signal.aborted) {
          return;
        }

        const newSavedColors = colors.map((color) => ({
          ...color,
          isLocal: false,
          error: null,
          shouldSync: true,
        }));

        initialUpdateSavedColors.current({ savedColors: newSavedColors });
        lastSavedColors.current = newSavedColors;
      })
      .catch((error) => {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      });

    return () => abortController.abort();
  }, []);

  const debouncedUpdateApiColors = useDebouncedCallback(
    (newSavedColors: SavedColor[]) => {
      const colorsToAdd = [];
      const colorsToEdit = [];
      const colorsToDelete = lastSavedColors.current.filter(
        (previousColor) =>
          !newSavedColors.find(
            ({ color: savedColor }) => previousColor.color.id === savedColor.id
          ) && !previousColor.isLocal
      );

      let colorStatusChanged = false;

      for (const savedColor of newSavedColors) {
        if (!savedColor.shouldSync) {
          continue;
        }

        const existingColorIndex = lastSavedColors.current.findIndex(
          ({ color }) => color.id === savedColor.color.id
        );

        if (existingColorIndex === -1) {
          colorsToAdd.push(savedColor);

          continue;
        }

        const previousColor = lastSavedColors.current[existingColorIndex];

        const isDifferent =
          !isPatternEqual(savedColor.color, previousColor.color) ||
          savedColor.shouldSync !== previousColor.shouldSync ||
          savedColor.name !== previousColor.name;

        if (isDifferent) {
          if (savedColor.isLocal) {
            colorsToAdd.push(savedColor);
          } else {
            colorsToEdit.push(savedColor);
          }
        } else if (!savedColor.isLocal && previousColor.isLocal) {
          colorStatusChanged = true;
        }
      }

      const isDifferent =
        colorsToAdd.length > 0 ||
        colorsToEdit.length > 0 ||
        colorsToDelete.length > 0;

      if (isDifferent || colorStatusChanged) {
        lastSavedColors.current = savedColors;
      }

      if (colorsToAdd.length > 0) {
        for (const newColor of colorsToAdd) {
          const colorId = newColor.color.id;

          const onSuccess = () => {
            updateSavedColorState({
              id: colorId,
              isLocal: false,
              error: null,
            });
          };

          const onRetry = () =>
            apiActions
              .addSavedColor({ color: newColor.color, name: newColor.name })
              .then(onSuccess);

          apiActions
            .addSavedColor({ color: newColor.color, name: newColor.name })
            .then(onSuccess)
            .catch(() => {
              updateSavedColorState({
                id: colorId,
                error: {
                  type: 'add',
                  onRetry,
                },
              });

              showSnackbar({
                message: `There was an issue adding ${
                  newColor.name || 'this color'
                } to your saved colors. Please try again.`,
                dismissable: false,
                actionLabel: 'Retry',
                onAction: onRetry,
              });
            });
        }
      }

      if (colorsToEdit.length > 0) {
        for (const editedColor of colorsToEdit) {
          const colorId = editedColor.id || editedColor.color.id;

          // Skip if colorId is undefined to prevent API calls to /undefined
          if (!colorId) {
            console.warn(
              'Skipping edit for saved color with undefined ID:',
              editedColor
            );
            continue;
          }

          const onSuccess = () => {
            updateSavedColorState({
              id: colorId,
              isLocal: false,
              error: null,
            });
          };

          const onRetry = () =>
            apiActions
              .editSavedColor({
                id: colorId,
                color: editedColor.color,
                name: editedColor.name,
              })
              .then(onSuccess);

          apiActions
            .editSavedColor({
              id: colorId,
              color: editedColor.color,
              name: editedColor.name,
            })
            .then(onSuccess)
            .catch(() => {
              updateSavedColorState({
                id: colorId,
                error: {
                  type: 'edit',
                  onRetry,
                },
              });

              showSnackbar({
                message: `There was an issue updating ${
                  editedColor.name || 'this saved color'
                }. Please try again.`,
                dismissable: false,
                actionLabel: 'Retry',
                onAction: onRetry,
              });
            });
        }
      }

      // if (colorsToDelete.length > 0) {
      //   for (const deletedColor of colorsToDelete) {
      //     const colorId = deletedColor.id || deletedColor.color.id;
      //
      //     // Skip if colorId is undefined to prevent API calls to /undefined
      //     if (!colorId) {
      //       console.warn('Skipping delete for saved color with undefined ID:', deletedColor);
      //       continue;
      //     }
      //
      //     const onRetry = () =>
      //       apiActions
      //         .deleteSavedColor({
      //           id: colorId,
      //           color: deletedColor.color,
      //         })
      //         .then(() => {
      //           // If the delete action previously failed, we re-added it to the local colours
      //           // so we need to delete it again
      //           updateSavedColors({
      //             action: 'delete',
      //             changedStyle: null,
      //             selectedElementIds: [],
      //             editedColor: {
      //               ...deletedColor,
      //               isLocal: false,
      //               shouldSync: true,
      //               error: {
      //                 type: 'delete',
      //                 onRetry,
      //               },
      //             },
      //           });
      //         });

      //     apiActions
      //       .deleteSavedColor({ id: colorId, color: deletedColor.color })
      //       .catch(() => {
      //         updateSavedColors({
      //           action: 'addOrEdit',
      //           changedStyle: null,
      //           selectedElementIds: [],
      //           editedColor: {
      //             ...deletedColor,
      //             isLocal: false,
      //             shouldSync: true,
      //             error: {
      //               type: 'delete',
      //               onRetry,
      //             },
      //           },
      //         });

      //         showSnackbar({
      //           message: `There was an issue deleting ${
      //             deletedColor.name || 'this color'
      //           } from your saved colors. Please try again.`,
      //           dismissable: false,
      //           actionLabel: 'Retry',
      //           onAction: onRetry,
      //         });
      //       });
      //   }
      // }
    },
    1000
  );

  useEffect(() => {
    debouncedUpdateApiColors(savedColors);
  }, [savedColors]);

  useEffect(() => {
    const css = `
    :root {
      ${savedColors
        .filter(({ color }) => !hasGradient(color))
        .map(({ color }) => {
          return `${CSS_VARIABLE_PREFIX}${color.id}: ${getPatternAsString(
            color
          )};`;
        })}
    }
  `;

    const existingStyleTag = document.querySelector(
      `style#${SAVED_COLORS_STYLE_TAG_ID}`
    );

    if (existingStyleTag) {
      // Status, names and gradient recipes do not change these solid-colour
      // variables. Avoid invalidating styles across the editor for those edits.
      if (existingStyleTag.innerHTML !== css) {
        existingStyleTag.innerHTML = css;
      }
      return;
    }

    const styleTag = document.createElement('style');
    styleTag.id = SAVED_COLORS_STYLE_TAG_ID;
    styleTag.innerHTML = css;
    document.head.appendChild(styleTag);
  }, [savedColors]);

  return <>{children}</>;
}

export default SavedColorsWrapper;
